import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, Mail, FileText, Clock, CheckCircle, 
  Send, Calendar, Users, Award, HeartHandshake,
  ArrowRight, Download, MessageSquare, Stethoscope,
  Activity, Brain, Bone, RefreshCw, X, MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Header from './Header';
import Footer from './Footer';
import ReCAPTCHA from 'react-google-recaptcha';
import usePageSeo from '../hooks/usePageSeo';

// ── Validators ────────────────────────────────────────────────────────────────

const validateName = (value, fieldLabel) => {
  if (!value.trim()) return `${fieldLabel} is required.`;
  if (value.trim().length < 2) return `${fieldLabel} must be at least 2 characters.`;
  if (value.trim().length > 50) return `${fieldLabel} must be under 50 characters.`;
  if (!/^[a-zA-Z\s'\-]+$/.test(value.trim()))
    return `${fieldLabel} can only contain letters, spaces, hyphens, or apostrophes.`;
  return '';
};

const validateEmail = (value) => {
  if (!value) return ''; // optional
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
    return 'Please enter a valid email address.';
  return '';
};

const validateMobile = (value) => {
  if (!value.trim()) return 'Mobile number is required.';
  const digits = value.replace(/[\s\-().+]/g, '');
  if (!/^\d+$/.test(digits))
    return 'Mobile number can only contain digits, spaces, +, -, or parentheses.';
  if (digits.length < 7 || digits.length > 15)
    return 'Mobile number must be between 7 and 15 digits.';
  return '';
};

const validateDOB = (value) => {
  if (!value) return 'Date of birth is required.';
  const dob = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (isNaN(dob.getTime())) return 'Please enter a valid date.';
  if (dob >= today) return 'Date of birth must be in the past.';
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 120);
  if (dob < minDate) return 'Please enter a realistic date of birth.';
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────

const initialFormData = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  email: '',
  mobile: '',
  referrerName: '',
  referrerEmail: '',
  notes: ''
};

const initialErrors = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  mobile: '',
  referrerName: '',
  referrerEmail: ''
};

const ForChiropractors = () => {
  usePageSeo({
    title: "For Chiropractors | Patient Referrals | ATL Regen",
    description: "Refer patients to ATL Regen for PRP therapy and regenerative medicine. Expert non-surgical care with detailed reports for chiropractors.",
    canonical: "https://www.atlregen.com/for-chiropractors",
    ogTitle: "ATL Regen for Chiropractors | Regenerative Medicine Referrals",
  });

  const referralSource = 'Chiropractor';
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaRenderKey, setCaptchaRenderKey] = useState(0);
  const [captchaReady, setCaptchaReady] = useState(false);
  const recaptchaRef = useRef(null);

  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (showReferralForm) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setTouched({});
      setCaptchaToken('');
      setCaptchaVerified(false);
      setCaptchaError('');
      setCaptchaReady(false);
      setSubmitSuccess(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  }, [showReferralForm]);

  useEffect(() => {
    if (!siteKey) setCaptchaError('Captcha key missing');
  }, [siteKey]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const resetCaptcha = (message) => {
    setCaptchaToken('');
    setCaptchaVerified(false);
    if (message) setCaptchaError(message);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaRenderKey((prev) => prev + 1);
  };

  const getFieldError = (name, value) => {
    switch (name) {
      case 'firstName':     return validateName(value, 'First name');
      case 'lastName':      return validateName(value, 'Last name');
      case 'referrerName':  return validateName(value, 'Referrer name');
      case 'email':         return validateEmail(value);
      case 'referrerEmail': return validateEmail(value);
      case 'mobile':        return validateMobile(value);
      case 'dateOfBirth':   return validateDOB(value);
      default:              return '';
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
  };

  const validateAll = () => {
    const newErrors = {
      firstName:    validateName(formData.firstName, 'First name'),
      lastName:     validateName(formData.lastName, 'Last name'),
      dateOfBirth:  validateDOB(formData.dateOfBirth),
      email:        validateEmail(formData.email),
      mobile:       validateMobile(formData.mobile),
      referrerName: validateName(formData.referrerName, 'Referrer name'),
      referrerEmail: validateEmail(formData.referrerEmail),
    };
    setErrors(newErrors);
    setTouched({
      firstName: true, lastName: true, dateOfBirth: true,
      email: true, mobile: true, referrerName: true, referrerEmail: true
    });
    return Object.values(newErrors).every((e) => e === '');
  };

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaToken(value);
      setCaptchaVerified(true);
      setCaptchaError('');
    } else {
      setCaptchaToken('');
      setCaptchaVerified(false);
    }
  };

  const showCaptchaBorder = Boolean(captchaError) && !/(timed out|timeout)/i.test(captchaError);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError('');

    if (!validateAll()) return;

    if (!captchaToken || !captchaVerified) {
      setCaptchaError('Please verify that you are not a robot.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        dob: formData.dateOfBirth || null,
        gender: formData.gender || null,
        email: formData.email || null,
        mobile: formData.mobile,
        referrer_name: formData.referrerName || null,
        referrer_email: formData.referrerEmail || null,
        notes: formData.notes || null,
        referral_source: referralSource,
        captcha_token: captchaToken
      };

      const resp = await fetch(`${apiBaseUrl}/api/personal-referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const errorBody = await resp.text();
        throw new Error(errorBody || 'submission failed');
      }

      setSubmitSuccess(true);
      resetCaptcha('');
    } catch (err) {
      console.error(err);
      if (String(err?.message || '').toLowerCase().includes('captcha')) {
        resetCaptcha('reCAPTCHA timed out. Please verify again.');
      }
      alert('There was a problem submitting the form');
      return;
    } finally {
      setIsSubmitting(false);
    }

    // setTimeout(() => {
    //   setShowReferralForm(false);
    //   setSubmitSuccess(false);
    //   setFormData(initialFormData);
    //   resetCaptcha('');
    // }, 2000);
  };

  // ── Render helpers ──────────────────────────────────────────────────────────
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-xs mt-1" role="alert">{errors[field]}</p>
    ) : null;

  const inputClass = (field) =>
    `h-11 ${errors[field] ? 'border-red-500 focus:ring-red-500' : ''}`;

  // ── Static data ─────────────────────────────────────────────────────────────
  const steps = [
    {
      number: "01",
      title: "Identify Referral Need",
      icon: Stethoscope,
      description: "When to refer to ATLREGEN",
      details: [
        "Patient not responding to chiropractic care",
        "Need for interventional pain management",
        "Potential regenerative medicine candidate",
        "Pre-surgical evaluation needed",
        "Complex spine cases requiring specialized care",
        "Patient requests non-surgical options beyond chiropractic"
      ]
    },
    {
      number: "02", 
      title: "Submit Referral",
      icon: Send,
      description: "Online Form, Email, Phone, or Fax",
      details: [
        "Online Form",
        "Email: chiro-referrals@atlregen.com",
        "Phone: (404) 355-2728 (ask for Chiropractic Liaison)",
        "Fax: (404) 662-2344"
      ],
      whatWeNeed: [
        "Patient name and contact",
        "Brief treatment history",
        "Current symptoms and diagnosis",
        "Imaging results (if available)",
        "Your preferred communication method"
      ]
    },
    {
      number: "03",
      title: "Collaborative Care",
      icon: HeartHandshake,
      description: "We Keep You Informed",
      details: [
        "Initial evaluation summary within 48 hours",
        "Treatment plan shared with referring chiropractor",
        "Progress updates at key milestones",
        "Clear communication about patient status"
      ],
      returnToCare: [
        "Patients return to chiropractic maintenance when appropriate",
        "Co-treatment protocols available",
        "Ongoing communication throughout care"
      ]
    }
  ];

  const whyRefer = [
    {
      icon: HeartHandshake,
      title: "Seamless Collaboration",
      points: [
        "Respect for chiropractic care - We complement, not replace",
        "Clear communication - Regular updates on patient progress",
        "Shared treatment goals - Patient-centered collaborative approach",
        "Quick turnaround - Reports within 48 hours"
      ]
    },
    {
      icon: Activity,
      title: "Advanced Treatment Options",
      subtitle: "When chiropractic care needs support:",
      points: [
        "Interventional Procedures: Epidurals, facet injections, nerve blocks",
        "Regenerative Medicine: PRP, stem cell therapy for joints and spine",
        "Spinal Decompression: Non-surgical disc treatment",
        "Medication Management: When indicated for pain control"
      ]
    },
    {
      icon: Award,
      title: "Conservative Philosophy",
      points: [
        "Non-surgical first approach aligns with chiropractic values",
        "Minimally invasive procedures when appropriate",
        "Focus on function and return to activity",
        "Avoiding opioids whenever possible"
      ]
    },
    {
      icon: MapPin,
      title: "Convenience for Your Patients",
      points: [
        "6 locations across GA and AL",
        "Flexible scheduling including early appointments",
        "Same-day appointments for urgent cases",
        "Free parking at all facilities"
      ]
    }
  ];

  const referralScenarios = [
    {
      icon: Bone,
      title: "Disc Herniations",
      when: "Persistent radicular symptoms despite chiropractic care, Progressive neurological deficits, Severe pain limiting function",
      treatments: ["Epidural steroid injections", "Selective nerve root blocks", "Spinal decompression therapy", "Regenerative disc treatments"]
    },
    {
      icon: Activity,
      title: "Joint Pain (Knee, Shoulder, Hip)",
      when: "Osteoarthritis not responding to conservative care, Sports injuries requiring advanced intervention, Patients seeking regenerative options",
      treatments: ["Joint injections (steroid, hyaluronic acid)", "PRP (Platelet-Rich Plasma) therapy", "Stem cell treatments", "Viscosupplementation"]
    },
    {
      icon: Brain,
      title: "Chronic Pain Conditions",
      when: "Complex regional pain syndrome, Failed back surgery syndrome, Chronic myofascial pain, Fibromyalgia requiring multimodal approach",
      treatments: ["Comprehensive pain evaluation", "Medication management", "Interventional procedures", "Coordinated care planning"]
    },
    {
      icon: RefreshCw,
      title: "Pre-Surgical Evaluation",
      when: "Patient considering surgery, Need for second opinion, Pre-operative optimization",
      treatments: ["Independent evaluation", "Non-surgical alternatives when appropriate", "Post-surgical rehabilitation coordination"]
    }
  ];

  const commitments = [
    {
      title: "Communication Promise",
      items: ["48-hour report turnaround on new evaluations", "Monthly updates on long-term patients", "Direct phone line to physician assistants", "Email updates for routine matters"]
    },
    {
      title: "Professional Respect",
      items: ["We never solicit your patients for ongoing care", "Patients return to you for maintenance when appropriate", "We acknowledge your role in patient care", "Co-treatment welcomed and encouraged"]
    },
    {
      title: "Educational Support",
      items: ["Case consultations available", "Lunch-and-learn presentations for your staff", "Continuing education collaboration", "Research updates in regenerative medicine"]
    }
  ];

  const faqs = [
    {
      q: "Will you try to take over my patient's care?",
      a: "Absolutely not. We provide specialized interventions and return patients to your care for maintenance when appropriate."
    },
    {
      q: "How do you communicate with referring chiropractors?",
      a: "We provide initial evaluation summaries within 48 hours and progress updates at key milestones. You're always in the loop."
    },
    {
      q: "Do you offer educational opportunities?",
      a: "Yes, we provide lunch-and-learns, case consultations, and continuing education collaboration."
    },
    {
      q: "What if my patient needs surgery?",
      a: "We focus on non-surgical options. If surgery is truly indicated, we refer to trusted surgeons and coordinate post-surgical rehabilitation."
    },
    {
      q: "How quickly can you see referred patients?",
      a: "We offer same-day or next-day appointments for urgent cases, typically within 3-5 days for routine referrals."
    },
    {
      q: "Do you accept insurance?",
      a: "Yes, we accept most major insurance plans. We also offer cash-pay options for non-covered services."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#20B2AA] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E63946] rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <HeartHandshake className="h-4 w-4" />
                <span>Chiropractor Referral Portal</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Collaborative Care for <span className="text-[#20B2AA]">Better Patient Outcomes</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                We complement your chiropractic care with advanced interventional and regenerative treatments. Patients return to you for ongoing maintenance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#20B2AA] hover:bg-[#1a9a94] text-white px-8 py-6 text-lg font-bold"
                  onClick={() => setShowReferralForm(true)}
                  data-testid="refer-patient-btn"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Refer a Patient
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1B1B3A] px-8 py-6 text-lg font-bold" asChild>
                  <a href="tel:+14043552728">
                    <Phone className="mr-2 h-5 w-5" />
                    (404) 355-2728
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#20B2AA]">48 Hours</div>
                <div className="text-sm text-gray-600">Report Turnaround</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#20B2AA]">100%</div>
                <div className="text-sm text-gray-600">Patient Return Policy</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#20B2AA]">6</div>
                <div className="text-sm text-gray-600">Convenient Locations</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#20B2AA]">14+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* 3-Step Process */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Simple Referral Process
              </h2>
              <p className="text-xl text-gray-600">Three steps to collaborative patient care</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {steps.map((step, index) => (
                <Card key={index} className="relative border-none shadow-xl bg-white hover:shadow-2xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="absolute -top-4 left-8 bg-[#20B2AA] text-white text-sm font-bold px-4 py-1 rounded-full">
                      Step {step.number}
                    </div>
                    <div className="bg-gradient-to-br from-[#20B2AA]/10 to-[#E63946]/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mt-4">
                      <step.icon className="h-8 w-8 text-[#1B1B3A]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Start Referral CTA */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-[#1B1B3A] via-[#2D2D4A] to-[#3B2D5A] p-6 md:p-8 shadow-2xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#E63946]/20 px-4 py-1.5 text-xs font-semibold text-[#FF6B76] mb-3">
                    <Send className="h-3.5 w-3.5" />
                    <span>Quick & Easy Online Referral</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">Ready to refer a patient?</h3>
                  <p className="text-gray-300 text-sm md:text-base">
                    Submit your referral online in minutes. Same-day appointments available — we'll handle the rest.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <Button
                    className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-bold rounded-full px-8"
                    onClick={() => setShowReferralForm(true)}
                    data-testid="start-referral-cta-btn"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Start Referral
                  </Button>
                  <a
                    href="tel:+14043552728"
                    className="inline-flex items-center gap-2 text-gray-200 hover:text-white transition-colors text-sm"
                  >
                    <Phone className="h-4 w-4 text-[#20B2AA]" />
                    Or call (404) 355-2728
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Chiropractors Refer */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Chiropractors Refer to ATLREGEN
              </h2>
              <p className="text-xl text-gray-600">A partnership built on mutual respect and patient outcomes</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {whyRefer.map((item, index) => (
                <Card key={index} className="border-gray-200 hover:border-[#20B2AA] transition-colors">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-br from-[#20B2AA] to-[#1a9a94] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                    <ul className="space-y-3">
                      {item.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-600">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-1" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Common Referral Scenarios */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Common Referral Scenarios
              </h2>
              <p className="text-xl text-gray-600">When advanced intervention can help your patients</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {referralScenarios.map((scenario, index) => (
                <Card key={index} className="border-none shadow-xl bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] w-12 h-12 rounded-xl flex items-center justify-center">
                        <scenario.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{scenario.title}</h3>
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-[#E63946]">When to refer:</span>
                      <p className="text-sm text-gray-600 mt-1">{scenario.when}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm font-semibold text-[#20B2AA]">What we offer:</span>
                      <ul className="mt-2 space-y-1">
                        {scenario.treatments.map((treatment, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <ArrowRight className="h-3 w-3 text-[#20B2AA]" />
                            {treatment}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Commitment to Referring Chiropractors
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {commitments.map((commitment, index) => (
                <Card key={index} className="border-2 border-[#20B2AA]/20 bg-[#20B2AA]/5">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-[#1B1B3A] mb-4">{commitment.title}</h3>
                    <ul className="space-y-2">
                      {commitment.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Card */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="border-none shadow-2xl bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] text-white overflow-hidden">
                <CardContent className="p-10 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h2 className="text-3xl font-bold mb-4">Chiropractor Liaison</h2>
                      <p className="text-gray-300 mb-6">
                        Our dedicated liaison is here to support your referrals and facilitate seamless communication.
                      </p>
                      <div className="space-y-4">
                        <a href="tel:+14043552728" className="flex items-center gap-3 text-lg hover:text-[#20B2AA] transition-colors">
                          <Phone className="h-5 w-5 text-[#20B2AA]" />
                          <span className="font-semibold">(404) 355-2728</span>
                        </a>
                        <a href="mailto:chiro-referrals@atlregen.com" className="flex items-center gap-3 hover:text-[#20B2AA] transition-colors">
                          <Mail className="h-5 w-5 text-[#20B2AA]" />
                          chiro-referrals@atlregen.com
                        </a>
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#20B2AA]" />
                          Fax: (404) 662-2344
                        </div>
                        <div className="flex items-center gap-3">
                          <Clock className="h-5 w-5 text-[#20B2AA]" />
                          Mon-Fri, 8:00 AM - 5:00 PM EST
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                      <h3 className="font-bold text-lg mb-4">Physician Consultations</h3>
                      <p className="text-sm text-gray-300 mb-4">
                        Available for complex cases. Call to schedule a physician-to-physician discussion.
                      </p>
                      <h3 className="font-bold text-lg mb-4 mt-6">What We Need:</h3>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Patient name and contact
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Brief treatment history
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Current symptoms and diagnosis
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Imaging results (if available)
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <MessageSquare className="h-6 w-6 text-[#20B2AA] flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Resources Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Downloadable Resources
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Chiropractor Referral Form
              </Button>
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Co-Treatment Protocol
              </Button>
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Referral Pad Template
              </Button>
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Patient Education Handouts
              </Button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-[#20B2AA]">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Collaborate?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let's work together to provide the best care for your patients
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-[#20B2AA] hover:bg-gray-100 px-8 py-6 text-lg font-bold" asChild>
                <a href="tel:+14043552728">
                  <Phone className="mr-2 h-5 w-5" />
                  (404) 355-2728
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#20B2AA] px-8 py-6 text-lg font-bold" asChild>
                <a href="mailto:chiro-referrals@atlregen.com">
                  <Mail className="mr-2 h-5 w-5" />
                  chiro-referrals@atlregen.com
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Referral Form Modal */}
      {showReferralForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-4xl shadow-2xl my-4">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#1B1B3A] to-[#20B2AA] px-6 py-4 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg">
                    <Send className="h-5 w-5 text-[#20B2AA]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Refer a Patient</h2>
                </div>
                <button
                  onClick={() => setShowReferralForm(false)}
                  className="text-white/70 hover:text-white transition-colors p-1"
                  data-testid="close-referral-modal"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Form Content */}
            {submitSuccess ? (
              <div className="p-8 sm:p-12 text-center">
                <div className="bg-green-100 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Referral Submitted!</h3>
                <p className="text-gray-600 text-sm">Our team will contact the patient within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} onFocus={() => setCaptchaReady(true)} className="p-6" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                  {/* First Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('firstName')}
                      aria-invalid={!!errors.firstName}
                      data-testid="first-name-input"
                    />
                    <FieldError field="firstName" />
                  </div>

                  {/* Last Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('lastName')}
                      aria-invalid={!!errors.lastName}
                      data-testid="last-name-input"
                    />
                    <FieldError field="lastName" />
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Date of Birth <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('dateOfBirth')}
                      aria-invalid={!!errors.dateOfBirth}
                      data-testid="dob-input"
                    />
                    <FieldError field="dateOfBirth" />
                  </div>

                  {/* Gender */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Gender</Label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent"
                      data-testid="gender-select"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('email')}
                      aria-invalid={!!errors.email}
                      data-testid="email-input"
                    />
                    <FieldError field="email" />
                  </div>

                  {/* Mobile */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Mobile <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('mobile')}
                      aria-invalid={!!errors.mobile}
                      data-testid="mobile-input"
                    />
                    <FieldError field="mobile" />
                  </div>

                  {/* Referrer Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Referrer Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="referrerName"
                      value={formData.referrerName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('referrerName')}
                      aria-invalid={!!errors.referrerName}
                      data-testid="referrer-name-input"
                    />
                    <FieldError field="referrerName" />
                  </div>

                  {/* Referrer Email */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Referrer Email</Label>
                    <Input
                      name="referrerEmail"
                      type="email"
                      value={formData.referrerEmail}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('referrerEmail')}
                      aria-invalid={!!errors.referrerEmail}
                      data-testid="referrer-email-input"
                    />
                    <FieldError field="referrerEmail" />
                  </div>

                  {/* Notes */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Notes</Label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent resize-y"
                      data-testid="notes-input"
                    />
                  </div>

                  {/* reCAPTCHA — lazy: only mounts after first field focus */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <div className={`${showCaptchaBorder ? 'border-2 border-red-500 rounded-lg p-2' : ''}`}>
                      {captchaReady ? (
                        <ReCAPTCHA
                          key={captchaRenderKey}
                          ref={recaptchaRef}
                          sitekey={siteKey}
                          onChange={handleCaptchaChange}
                          onExpired={() => resetCaptcha('reCAPTCHA timed out. Please verify again.')}
                          onErrored={() => resetCaptcha('Captcha failed to load. Check key/domain or disable ad blocker and reload.')}
                          data-testid="chiro-recaptcha"
                        />
                      ) : (
                        <div
                          className="flex items-center gap-3 border border-gray-300 rounded bg-gray-50 px-4 py-3 cursor-pointer text-sm text-gray-600"
                          onClick={() => setCaptchaReady(true)}
                        >
                          <span className="w-5 h-5 border-2 border-gray-400 rounded inline-block flex-shrink-0" />
                          I'm not a robot
                        </div>
                      )}
                    </div>
                    {captchaError && (
                      <p className="text-red-500 text-sm mt-2">{captchaError}</p>
                    )}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 px-6 bg-gray-600 text-white hover:bg-gray-700 border-none rounded-full"
                    onClick={() => setShowReferralForm(false)}
                    data-testid="cancel-form-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold h-11 px-8 rounded-full"
                    disabled={isSubmitting || !captchaVerified}
                    data-testid="submit-form-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ForChiropractors;